import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CreatePartnerCategoryModal } from './PartnerCategoryModal'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import { PartnerCategory } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import {
  deletePartnerCategory,
  getPartnerCategories,
  publishPartnerCategory,
  unpublishPartnerCategory,
} from '../../../core/services/partners'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'

export const PartnerCategoryPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()

  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [showCreatePartnerCategoryModal, setShowCreatePartnerCategoryModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [currentPartnerCategory, setCurrentPartnerCategory] =
    useState<PartnerCategory>()
  const [partnerCategories, setPartnerCategories] = useState<PartnerCategory[]>(
    [],
  )
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getPartnerCategories({
        page,
        size,
        sortBy,
        sortDirection,
      })
      setPartnerCategories(response.data.content)
      setTotalElemenets(response.data.totalElements)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [size, page, sortBy, sortDirection, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = async () => {
    try {
      const { status } = await deletePartnerCategory(isCheck)
      getData()
      setIsCheck([])
      if (status === 204) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handlePublish = useCallback(
    async (checkedIds: string[]) => {
      try {
        setLoading(true)
        const { status } = await publishPartnerCategory(checkedIds)
        getData()
        setIsCheck([])
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    },
    [getData],
  )

  const handleUnpublish = useCallback(
    async (checkedIds: string[]) => {
      try {
        setLoading(true)
        const { status } = await unpublishPartnerCategory(checkedIds)
        getData()
        setIsCheck([])
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    },
    [getData],
  )

  const handleSelectAll = useCallback(() => {
    const nextChecked = !isCheckAll
    setIsCheckAll(nextChecked)
    setIsCheck(
      nextChecked ? partnerCategories.map((partner) => partner.id) : [],
    )
  }, [isCheckAll, partnerCategories])

  const handleClick = useCallback((id: string, checked: boolean) => {
    setIsCheck((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id),
    )
  }, [])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: (
          <Checkbox checked={isCheckAll} onCheckedChange={handleSelectAll} />
        ),
        accessor: '',
        render: (row) => (
          <Checkbox
            checked={isCheck.includes(row.id)}
            onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          />
        ),
        width: '16px',
      },
      {
        title: 'Name',
        accessor: 'name',
        sortable: true,
      },
      {
        title: 'Weight',
        accessor: 'weight',
        sortable: true,
        render: (row) => (
          <Input
            type="text"
            value={row.weight}
            readOnly
            className="bg-monochrome-4 border-none"
          />
        ),
      },
      {
        title: 'Published',
        accessor: '',
        render: (row) => (
          <Checkbox
            checked={row.published}
            onCheckedChange={() => {
              if (row.published) {
                handleUnpublish([row.id])
              } else {
                handlePublish([row.id])
              }
            }}
          />
        ),
      },
      {
        title: 'Publish Date',
        accessor: '',
        render: (row) => sanitizeDate(new Date(row.publishedAt)),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row) => (
          <button
            type="button"
            className="view text-accent-1 underline"
            onClick={() => {
              setEditMode(true)
              setCurrentPartnerCategory(row)
              setShowCreatePartnerCategoryModal(true)
            }}
          >
            Edit
          </button>
        ),
      },
    ],
    [
      handleClick,
      handlePublish,
      handleSelectAll,
      handleUnpublish,
      isCheck,
      isCheckAll,
    ],
  )

  return (
    <>
      <Header
        pageTitle="Partner Categories"
        actions={
          <>
            {isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            <Button
              variant="primary-bordered"
              onClick={() => {
                setEditMode(false)
                setShowCreatePartnerCategoryModal(true)
                setCurrentPartnerCategory(undefined)
              }}
            >
              Add New
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle
          title="Partners"
          subtitle="Partner Categories"
          className="mb-5"
        />
        <Table
          columnWidth="25%"
          isCheck={isCheck}
          columns={columns}
          handleSortBy={handleSortBy}
          data={partnerCategories}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreatePartnerCategoryModal
        partnerCategory={currentPartnerCategory}
        setEditMode={setEditMode}
        setShowCreateModal={setShowCreatePartnerCategoryModal}
        setPartnerCategories={setPartnerCategories}
        showCreateModal={showCreatePartnerCategoryModal}
        editMode={editMode}
        page={page + 1}
        size={size}
        setTotalElemenets={setTotalElemenets}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Partner Category'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
